import { Injectable } from '@angular/core';
import { EbwModalService } from '@ebw-monorepo/modal';
import { PermissionService, UtilsService } from '@ebw-monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { safeJsonParse, safeJsonStringify } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../utils/src/lib/services/api-url.service';
import { IExportSettings } from '../interfaces/exportSettings.interface';
import { IMapSettings } from '../interfaces/mapSettings.interface';
import { IExternalSettings } from '../interfaces/externalSettings.interface';
import { IStylesConfig } from '../interfaces/style.interface';
import { LayerConfigurator } from './../components/sidebar/ebw-layers-configurator/layer-configurator.model';
import { ISidebarConfig } from './../interfaces/httpReq.interface';
export enum ENUM_CONFIG {
  LAYER = 'LAYER',
  STYLES = 'STYLES',
  MAP = 'MAP',
  EXPORT = 'EXPORT',
  EXTERNAL = 'EXTERNAL'
  //   BOOKMARK = 'BOOKMARK',
  //   QUERY = 'QUERY',
  //   DATASET = 'DATASET',
  //   VALVES = 'VALVES',
  //   EXPORTAPI = 'EXPORTAPI',
  //   TELCO = 'TELCO',
  //   CHECKIN = 'CHECKIN',
  //   PERMISSION = 'PERMISSION',
  //   PLOTTING = 'PLOTTING',
  //   PSA = 'PSA',
  //   USER = 'USER'
}

@Injectable({
  providedIn: 'root'
})
export class ConfigSidebarService {
  private static SIDEBAR_CONFIG = 'SIDEBAR_CONFIG';

  private layerVisibilityPermissions = null;

  public sidebarConfiguration$: BehaviorSubject<ISidebarConfig> = new BehaviorSubject<ISidebarConfig>(null);
  public layerConfiguration$: BehaviorSubject<LayerConfigurator> = new BehaviorSubject<LayerConfigurator>(null);
  public styleConfiguration$: BehaviorSubject<IStylesConfig> = new BehaviorSubject<IStylesConfig>(null);
  public mapSettings$: BehaviorSubject<IMapSettings> = new BehaviorSubject<IMapSettings>(null);
  public exportSettings$: BehaviorSubject<IExportSettings> = new BehaviorSubject<IExportSettings>(null);
  public externalSettings$: BehaviorSubject<IExternalSettings> = new BehaviorSubject<IExternalSettings>(null);

  constructor(
    private _utilsService: UtilsService,
    private _ebwModalService: EbwModalService,
    private _translateService: TranslateService,
    private _apiUrlService: ApiUrlService,
    private _permissionService: PermissionService
  ) {}

  public setSidebarConfiguration(config: ISidebarConfig) {
    const payload = safeJsonStringify(config);
    if (payload !== null) {
      sessionStorage.setItem(ConfigSidebarService.SIDEBAR_CONFIG, payload);
    }
    this.sidebarConfiguration$.next(config);
  }

  public getSidebarConfiguration(): ISidebarConfig {
    if (!this.sidebarConfiguration$.value) {
      const config: ISidebarConfig = safeJsonParse(sessionStorage.getItem(ConfigSidebarService.SIDEBAR_CONFIG));
      this.setSidebarConfiguration(config);
    }
    return this.sidebarConfiguration$.value;
  }

  public getLayerConfiguration() {
    if (!this.layerConfiguration$.value) {
      const config: LayerConfigurator = safeJsonParse(sessionStorage.getItem(ENUM_CONFIG.LAYER));
      this.setLayerConfiguration(config);
    }
    return this.layerConfiguration$.value;
  }

  public setLayerConfiguration(config: LayerConfigurator) {
    const payload = safeJsonStringify(config);
    if (payload !== null) {
      sessionStorage.setItem(ENUM_CONFIG.LAYER, payload);
    }
    this.layerConfiguration$.next(config);
  }

  public setStyleConfiguration(config: IStylesConfig) {
    const payload = safeJsonStringify(config);
    if (payload !== null) {
      sessionStorage.setItem(ENUM_CONFIG.STYLES, payload);
    }
    this.styleConfiguration$.next(config);
  }

  public getStyleConfiguration() {
    if (!this.styleConfiguration$.value) {
      const styles: IStylesConfig = safeJsonParse(sessionStorage.getItem(ENUM_CONFIG.STYLES));
      this.setStyleConfiguration(styles);
    }

    return this.styleConfiguration$.value;
  }

  public setMapSettings(config: IMapSettings) {
    const payload = safeJsonStringify(config);
    if (payload !== null) {
      sessionStorage.setItem(ENUM_CONFIG.MAP, payload);
    }
    this.mapSettings$.next(config);
  }

  public getMapSettings() {
    if (!this.mapSettings$.value) {
      const mapSettings: IMapSettings = safeJsonParse(sessionStorage.getItem(ENUM_CONFIG.MAP));
      this.setMapSettings(mapSettings);
    }

    return this.mapSettings$.value;
  }

  public setExportSettings(config: IExportSettings) {
    const payload = safeJsonStringify(config);
    if (payload !== null) {
      sessionStorage.setItem(ENUM_CONFIG.EXPORT, payload);
    }
    this.exportSettings$.next(config);
  }

  public getExportSettings() {
    if (!this.exportSettings$.value) {
      const exportSettings: IExportSettings = safeJsonParse(sessionStorage.getItem(ENUM_CONFIG.EXPORT));
      this.setExportSettings(exportSettings);
    }

    return this.exportSettings$.value;
  }

  public setExternalSettings(config: IExternalSettings) {
    const payload = safeJsonStringify(config);
    if (payload !== null) {
      sessionStorage.setItem(ENUM_CONFIG.EXTERNAL, payload);
    }
    this.externalSettings$.next(config);
  }

  public getExternalSettings() {
    if (!this.externalSettings$.value) {
      const externalSettings: IExternalSettings = safeJsonParse(sessionStorage.getItem(ENUM_CONFIG.EXTERNAL));
      this.setExternalSettings(externalSettings);
    }

    return this.externalSettings$.value;
  }

  public async getLayerVisibilityPermissions(): Promise<any[]> {
    try {
      const permissionsResponse = await firstValueFrom(this._utilsService.getRequest(this._apiUrlService.createUrl(API_URLS.PERMISSION.LIST), {}));

      let permissions = [];

      permissions = permissionsResponse;

      const layerPermissionsPrefix = 'VISIBILITY_LAYER_';

      const layerVisibilityPermissions = permissions.filter((el) => {
        return el.name.includes(layerPermissionsPrefix);
      });

      return layerVisibilityPermissions;
    } catch (err) {
      console.error('[ConfigSidebarService][getLayerVisibilityPermissions]', err);
      this._ebwModalService.openErrorModal({
        content: this._translateService.instant('common.data_retrieving_error'),
        title: this._translateService.instant('common.error')
      });
      return [];
    }
  }

  public async checkLayerVisibility(name: string): Promise<boolean> {
    if (!this.layerVisibilityPermissions) {
      this.layerVisibilityPermissions = await this.getLayerVisibilityPermissions();
    }

    const layerPermissionsPrefix = 'VISIBILITY_LAYER_';

    const layerPermission = this.layerVisibilityPermissions.find((el) => {
      return el.name === layerPermissionsPrefix + name;
    });

    if (!layerPermission) {
      return true;
    }
    // TODO ma questo è ancora usato??
    if (this._permissionService.checkPermission(layerPermission.name)) {
      return true;
    }

    return false;
  }

  public checkLayerVisibilitySync(name: string) {
    if (!this.layerVisibilityPermissions) {
      return true;
    }

    const layerPermissionsPrefix = 'VISIBILITY_LAYER_';

    const layerPermission = this.layerVisibilityPermissions.find((el) => {
      return el.name === layerPermissionsPrefix + name;
    });

    if (!layerPermission) {
      return true;
    }
    // TODO ma questo è ancora usato??
    if (this._permissionService.checkPermission(layerPermission.name)) {
      return true;
    }

    return false;
  }

  /**
   * Remove this.CONFIGURATION_ITEM_STRING item from the sessionStorage.
   */
  public removeConfiguration() {
    this.sidebarConfiguration$.next(null);
    this.layerConfiguration$.next(null);
  }
}
